import React, { useState, useCallback, useContext } from "react";
import {
  PageHeader,
  Icons,
  Button,
  useClientTable,
  DataTable,
  Menu,
  useToggle,
} from "@pushpress/shared-components";
import { AppContext } from "../../context/app";
import PageContainer from "../../components/common/PageContainer/PageContainer";
import {
  LocationForLocationsPageFragment,
  useGetLocationsForLocationsPageQuery,
} from "./Locations-generated.hooks";
import LocationDrawer from "./LocationDrawer";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../utils/misc";
import LocationDeleteModal from "./LocationDeleteModal";
import BackArrow from "../../assets/img/icons/icon-arrow-east.svg";
import { Link } from "react-router-dom";

function Locations() {
  const app = useContext(AppContext);

  const [editData, setEditData] = useState<LocationForLocationsPageFragment>();
  const [drawerVisible, drawerActions] = useToggle(false);
  const [deleteVisible, deleteActions] = useToggle(false);

  const {
    data: { getLocations: locations = [] } = {},
    refetch,
    loading,
  } = useGetLocationsForLocationsPageQuery({
    variables: {
      clientUuid: app.client.uuid,
    },
    onError: (error) => {
      toast.error(extractError(error), defaultToastOptions);
    },
  });

  const columns = [
    {
      key: "name",
      header: "Name",
      isSortable: true,
    },
  ];

  const { sort, data, handleSort } = useClientTable({
    data: locations,
    columns,
    defaultSort: { key: "name", order: "asc" },
  });

  const handleEditClick = (data: LocationForLocationsPageFragment) => {
    setEditData(data);
    drawerActions.on();
  };

  const handleDeleteClick = (data: LocationForLocationsPageFragment) => {
    setEditData(data);
    deleteActions.on();
  };

  const handleAdd = useCallback(() => {
    setEditData(undefined);
    drawerActions.on();
  }, [drawerActions]);

  const handleSuccess = () => {
    drawerActions.off();
    refetch();
  };

  const getRowId = (row: LocationForLocationsPageFragment) => {
    return row.uuid;
  };

  const renderRowMenu = (
    row: LocationForLocationsPageFragment,
    closeMenu: VoidFunction,
  ) => {
    return (
      <Menu>
        <Menu.Item
          label="Edit"
          action={() => {
            handleEditClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Delete"
          action={() => {
            handleDeleteClick(row);
            closeMenu();
          }}
        ></Menu.Item>
      </Menu>
    );
  };

  return (
    <>
      <PageContainer>
        <Button
          component={Link}
          text="Back to calendar"
          buttonType="text"
          to={"/schedule"}
          icon={BackArrow}
          iconLocation="left"
          className="mb-2"
        />
        <PageHeader title="Locations">
          <PageHeader.ActionMenu>
            <Button
              text="Location"
              size="large"
              iconLocation="left"
              hideTextOnMobile
              icon={Icons.Plus}
              iconBold
              onClick={handleAdd}
            />
          </PageHeader.ActionMenu>
        </PageHeader>
        <DataTable
          data={data}
          columns={columns}
          sort={sort}
          loading={loading}
          getRowId={getRowId}
          onSort={handleSort}
          renderRowMenu={renderRowMenu}
        />
      </PageContainer>
      <LocationDrawer
        visible={drawerVisible}
        location={editData}
        onClose={drawerActions.off}
        onSuccess={handleSuccess}
      />
      <LocationDeleteModal
        visible={deleteVisible}
        locationUuid={editData?.uuid}
        onClose={deleteActions.off}
        onSuccess={handleSuccess}
      />
    </>
  );
}

export default Locations;
